import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { StickyNote, ChevronDown, ChevronUp, Save, Send, Reply } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api';

export const BOOKING_STATUSES = ['pending', 'confirmed', 'cancelled'];
export const INQUIRY_STATUSES = ['new', 'replied', 'closed'];

export const statusClass = (s) => ({
  pending: 'bg-yellow-100 text-yellow-700',
  confirmed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
  new: 'bg-blue-100 text-blue-700',
  replied: 'bg-teal-100 text-teal-700',
  closed: 'bg-gray-100 text-gray-700',
}[s] || 'bg-gray-100 text-gray-700');

export const StatusSelect = ({ value, options, onChange, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId} className={`w-36 h-9 font-semibold capitalize border-0 ${statusClass(value)}`}>
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      {options.map((o) => (
        <SelectItem key={o} value={o} className="capitalize" data-testid={`${testId}-option-${o}`}>{o}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const NotesEditor = ({ booking, onSaved }) => {
  const [notes, setNotes] = useState(booking.admin_notes || '');
  const [saving, setSaving] = useState(false);
  const dirty = notes !== (booking.admin_notes || '');

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.patch(`/admin/bookings/${booking.booking_id}/notes`, { admin_notes: notes });
      onSaved(res.data);
      toast.success('Notes saved');
    } catch (e) {
      toast.error('Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="md:col-span-12 border-t border-gray-100 pt-4 mt-1" data-testid={`notes-editor-${booking.booking_id}`}>
      <Textarea
        data-testid={`notes-input-${booking.booking_id}`}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={3}
        maxLength={2000}
        placeholder="Call log, follow-ups, payment status... (only visible to admins)"
        className="border-2 bg-amber-50/40"
      />
      <div className="flex justify-end mt-2">
        <Button data-testid={`notes-save-${booking.booking_id}`} size="sm" onClick={save} disabled={!dirty || saving} className="bg-gray-900 text-white hover:bg-gray-800">
          <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save notes'}
        </Button>
      </div>
    </div>
  );
};

export const BookingRow = ({ b, onStatus, onSaved }) => {
  const [open, setOpen] = useState(false);
  const hasNotes = Boolean(b.admin_notes?.trim());
  return (
    <Card className="border-0 shadow-sm" data-testid={`admin-booking-${b.booking_id}`}>
      <CardContent className="p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
        <div className="md:col-span-4">
          <p className="font-bold text-gray-900">{b.destination}</p>
          {b.package_name && <p className="text-sm text-teal-700">{b.package_name}</p>}
          <p className="text-xs text-gray-400 font-mono mt-1">{b.booking_id}</p>
        </div>
        <div className="md:col-span-3 text-sm text-gray-600">
          <p className="font-semibold text-gray-900">{b.name}</p>
          <p>{b.email}</p>
          <p>{b.phone}</p>
        </div>
        <div className="md:col-span-2 text-sm text-gray-600">
          <p>Travel: <span className="font-semibold text-gray-900">{b.travel_date}</span></p>
          <p>{b.travelers} traveler(s)</p>
          {b.special_requests && <p className="italic text-gray-500 truncate" title={b.special_requests}>"{b.special_requests}"</p>}
        </div>
        <div className="md:col-span-3 flex items-center gap-2 md:justify-end">
          <button
            data-testid={`notes-toggle-${b.booking_id}`}
            onClick={() => setOpen((v) => !v)}
            className={`inline-flex items-center gap-1 text-sm px-3 h-9 rounded-md border transition-colors ${hasNotes ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
          >
            <StickyNote className="w-4 h-4" /> Notes {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <StatusSelect value={b.status} options={BOOKING_STATUSES} onChange={(s) => onStatus(b.booking_id, s)} testId={`booking-status-${b.booking_id}`} />
        </div>
        {open && <NotesEditor booking={b} onSaved={onSaved} />}
      </CardContent>
    </Card>
  );
};

const ReplyBox = ({ q, onReplied }) => {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const send = async () => {
    setSending(true);
    try {
      const res = await api.post(`/admin/inquiries/${q.inquiry_id}/reply`, { message: message.trim() });
      onReplied(res.data);
      setMessage('');
      const last = res.data.replies[res.data.replies.length - 1];
      if (last?.email_status === 'failed') toast.warning('Reply saved, but the email could not be sent right now. Please resend later.');
      else toast.success(`Reply emailed to ${q.email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="md:col-span-12 border-t border-gray-100 pt-4 space-y-3" data-testid={`reply-box-${q.inquiry_id}`}>
      {q.replies?.length > 0 && (
        <div className="space-y-2" data-testid={`reply-history-${q.inquiry_id}`}>
          {q.replies.map((r, i) => (
            <div key={i} className="bg-teal-50 border-l-4 border-teal-500 rounded-r-lg px-4 py-3 text-sm">
              <p className="text-gray-800 whitespace-pre-wrap">{r.message}</p>
              <p className="text-xs text-gray-500 mt-1">
                Sent {new Date(r.sent_at).toLocaleString()} by {r.sent_by}
                {r.email_status === 'failed' && <span className="ml-2 text-red-600 font-semibold">· email not delivered</span>}
              </p>
            </div>
          ))}
        </div>
      )}
      <Textarea
        data-testid={`reply-input-${q.inquiry_id}`}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={4}
        maxLength={3000}
        placeholder={`Write your reply to ${q.name}... it will be emailed to ${q.email}`}
        className="border-2"
      />
      <div className="flex justify-end">
        <Button data-testid={`reply-send-${q.inquiry_id}`} size="sm" onClick={send} disabled={message.trim().length < 2 || sending} className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white">
          <Send className="w-4 h-4 mr-1" /> {sending ? 'Sending...' : 'Send reply by email'}
        </Button>
      </div>
    </div>
  );
};

export const InquiryRow = ({ q, onStatus, onReplied }) => {
  const [open, setOpen] = useState(false);
  const replyCount = q.replies?.length || 0;
  return (
    <Card className="border-0 shadow-sm" data-testid={`admin-inquiry-${q.inquiry_id}`}>
      <CardContent className="p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
        <div className="md:col-span-3 text-sm text-gray-600">
          <p className="font-bold text-gray-900">{q.name}</p>
          <p>{q.email}</p>
          <p>{q.phone}</p>
          <p className="text-xs text-gray-400 mt-1">{new Date(q.created_at).toLocaleString()}</p>
        </div>
        <p className="md:col-span-5 text-gray-700 whitespace-pre-wrap">{q.message}</p>
        <div className="md:col-span-4 flex items-center gap-2 md:justify-end">
          <button
            data-testid={`reply-toggle-${q.inquiry_id}`}
            onClick={() => setOpen((v) => !v)}
            className={`inline-flex items-center gap-1 text-sm px-3 h-9 rounded-md border transition-colors ${replyCount ? 'border-teal-300 bg-teal-50 text-teal-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
          >
            <Reply className="w-4 h-4" /> Reply{replyCount ? ` (${replyCount})` : ''} {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <StatusSelect value={q.status} options={INQUIRY_STATUSES} onChange={(s) => onStatus(q.inquiry_id, s)} testId={`inquiry-status-${q.inquiry_id}`} />
        </div>
        {open && <ReplyBox q={q} onReplied={onReplied} />}
      </CardContent>
    </Card>
  );
};
